import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

import { GET_BANNER } from '../../graphql/queries';

const DISMISS_KEY = 'dismissedBannerContent';

// Announcement set in the CMS (Banner single type). Dismissing hides that text until it changes.
export const AppBanner = () => {
  const { t } = useTranslation();
  const { data } = useQuery(GET_BANNER);
  const [dismissKey, setDismissKey] = useState(0);
  const barRef = useRef(null);

  const banner = data?.banner?.data?.attributes?.banner;
  const isPt = t('lang') === 'pt';
  const text = isPt ? banner?.bannerTextPt : banner?.bannerTextEn;
  const linkText = isPt ? banner?.bannerLinkTextPt : banner?.bannerLinkTextEn;
  const linkUrl = isPt ? banner?.bannerLinkPt : banner?.bannerLinkEn;

  const isVisible = useMemo(() => {
    if (banner?.shouldShowBanner !== 'true' || !text) return false;
    try {
      return localStorage.getItem(DISMISS_KEY) !== text;
    } catch {
      return true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [banner, text, dismissKey]);

  // Publishes the bar's height so the desktop tab bar (fixed at the top) sits below it instead of over it
  useEffect(() => {
    const root = document.documentElement;
    const bar = barRef.current;
    if (!isVisible || !bar) {
      root.style.removeProperty('--mt-banner-h');
      return undefined;
    }
    const publish = () => root.style.setProperty('--mt-banner-h', `${bar.offsetHeight}px`);
    publish();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(publish) : null;
    observer?.observe(bar);
    return () => {
      observer?.disconnect();
      root.style.removeProperty('--mt-banner-h');
    };
  }, [isVisible]);

  if (!isVisible) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, text);
    } catch {
      // Storage blocked: hide it for this visit only
    }
    setDismissKey((key) => key + 1);
  };

  return (
    <div ref={barRef} className="mt-root mt-app-banner" role="region" aria-label={text}>
      <p className="mt-app-banner__text">
        {text}
        {linkText && linkUrl && (
          <>
            {' '}
            <a href={linkUrl}>{linkText}</a>
          </>
        )}
      </p>
      <button type="button" className="mt-app-banner__close" onClick={dismiss} aria-label={t('standalone.dismissBanner')}>
        <X aria-hidden="true" />
      </button>
    </div>
  );
};
